<template>
  <div style="display: contents">
    <tr v-if="loading" key="boq-tree-loading">
      <td colspan="8" class="px-4 py-3 text-center text-gray-500 border border-gray-200">
        {{ $t('loading') }}...
      </td>
    </tr>

    <template v-else>
      <template v-for="item in items">
        <!-- Nodo takeoff (disciplina / etapa) -->
        <template v-if="item.nodeType === 'takeoff_node'">
          <tr
            :key="`takeoff-${item.id}`"
            class="boq-takeoff-row"
            :class="{
              'boq-takeoff-row--nested': item.depth >= 1,
              'cursor-pointer': hasChildren(item),
            }"
            @click="hasChildren(item) && ctx.toggleGroup(item.id)"
          >
            <td class="px-2 py-2 border border-gray-300" colspan="8">
              <div class="flex items-center gap-2 min-w-0" :style="indentStyle(item.depth)">
                <button
                  v-if="hasChildren(item)"
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 bg-transparent border-0 cursor-pointer focus:outline-none boq-takeoff-row__chevron"
                  :aria-expanded="ctx.isGroupExpanded(item.id)"
                  @click.stop="ctx.toggleGroup(item.id)"
                >
                  <b-icon
                    pack="fas"
                    :icon="ctx.isGroupExpanded(item.id) ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                  />
                </button>
                <span v-else class="inline-block flex-shrink-0 w-6" />
                <span class="flex-shrink-0 px-2 py-0.5 text-[0.65rem] font-bold text-white rounded boq-takeoff-row__code-badge">
                  {{ item.badgeLabel || item.code }}
                </span>
                <span class="flex-1 min-w-0 overflow-hidden font-semibold truncate boq-takeoff-row__title">
                  {{ item.title }}
                </span>
                <span class="flex-shrink-0 boq-takeoff-row__meta">{{ item.measurementsLabel }}</span>
                <button
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 text-white border-0 rounded-full cursor-pointer focus:outline-none boq-takeoff-row__action-btn"
                  :title="$t('edit')"
                  :aria-label="$t('edit')"
                  @click.stop="ctx.openEditTakeoffChild(item)"
                >
                  <b-icon pack="fas" icon="pen" size="is-small" />
                </button>
                <button
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 text-white border-0 rounded-full cursor-pointer focus:outline-none boq-takeoff-row__action-btn boq-takeoff-row__action-btn--danger"
                  :class="{ 'opacity-50 cursor-wait': ctx.isDeletingTakeoffChild(item.id) }"
                  :title="$t('delete')"
                  :aria-label="$t('delete')"
                  :disabled="ctx.isDeletingTakeoffChild(item.id)"
                  @click.stop="ctx.confirmDeleteTakeoffChild(item)"
                >
                  <b-icon
                    v-if="ctx.isDeletingTakeoffChild(item.id)"
                    pack="fas"
                    icon="spinner"
                    custom-class="fa-spin"
                    size="is-small"
                  />
                  <b-icon
                    v-else
                    pack="fas"
                    icon="trash-alt"
                    size="is-small"
                  />
                </button>
                <button
                  v-if="item.canAddChild"
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 text-white border-0 rounded-full cursor-pointer focus:outline-none boq-takeoff-row__action-btn"
                  :title="item.addChildTitle"
                  :aria-label="item.addChildTitle"
                  @click.stop="ctx.openCreateResource(item)"
                >
                  <b-icon pack="fas" icon="plus" size="is-small" />
                </button>
                <span
                  v-if="hasChildren(item)"
                  class="inline-flex flex-shrink-0 items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-[0.65rem] font-bold text-white rounded-full boq-takeoff-row__count"
                >{{ item.childrenCount != null ? item.childrenCount : item.count }}</span>
              </div>
            </td>
          </tr>
          <boq-tree-items
            v-if="hasChildren(item) && ctx.isGroupExpanded(item.id)"
            :key="`takeoff-children-${item.id}-${refreshToken}`"
            :project-id="projectId"
            :parent-boq-root-id="item.id"
            :root-context="item.rootContext || rootContext"
            :depth="item.depth + 1"
            :refresh-token="ctx.treeRefreshToken(item.id)"
          />
        </template>

        <!-- CertifierHead anidado (árbol manual P6) -->
        <template v-else-if="item.nodeType === 'certifier_head'">
          <tr
            :key="`certifier-${item.id}`"
            class="cursor-pointer"
            :class="item.isManualTree
              ? ['boq-manual-row', { 'boq-manual-row--nested': item.depth >= 1 }]
              : ['bg-blue-lightest', 'hover:bg-blue-lighter']"
            @click="hasChildren(item) && ctx.toggleGroup(item.id)"
          >
            <td class="px-2 py-2 border border-gray-300" colspan="8">
              <div class="flex items-center gap-2 min-w-0" :style="indentStyle(item.depth)">
                <button
                  v-if="hasChildren(item)"
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 bg-transparent border-0 cursor-pointer focus:outline-none"
                  :class="item.isManualTree ? 'boq-manual-row__chevron' : 'text-blue-dark'"
                  :aria-expanded="ctx.isGroupExpanded(item.id)"
                  @click.stop="ctx.toggleGroup(item.id)"
                >
                  <b-icon
                    pack="fas"
                    :icon="ctx.isGroupExpanded(item.id) ? 'chevron-down' : 'chevron-right'"
                    size="is-small"
                  />
                </button>
                <span v-else class="inline-block flex-shrink-0 w-6" />
                <span
                  class="flex-shrink-0 px-2 py-0.5 text-[0.65rem] font-bold text-white rounded"
                  :class="item.isManualTree ? 'boq-manual-row__code-badge' : 'bg-blue'"
                >{{ item.code }}</span>
                <span
                  class="flex-1 min-w-0 overflow-hidden font-semibold truncate"
                  :class="item.isManualTree ? 'boq-manual-row__title' : 'text-blue-dark'"
                >{{ item.title }}</span>
                <span
                  class="flex-shrink-0"
                  :class="item.isManualTree ? 'boq-manual-row__meta' : 'text-gray-500'"
                >{{ item.measurementsLabel }}</span>
                <button
                  type="button"
                  class="inline-flex flex-shrink-0 items-center justify-center w-6 h-6 text-white border-0 rounded-full cursor-pointer focus:outline-none"
                  :class="item.isManualTree ? 'boq-manual-row__action-btn' : 'bg-blue hover:bg-blue-dark'"
                  :title="item.isManualTree ? $t('add_boq_child') : $t('add_boq_resource')"
                  :aria-label="item.isManualTree ? $t('add_boq_child') : $t('add_boq_resource')"
                  @click.stop="ctx.openCreateResource(item)"
                >
                  <b-icon pack="fas" icon="plus" size="is-small" />
                </button>
                <span
                  class="inline-flex flex-shrink-0 items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-[0.65rem] font-bold text-white rounded-full"
                  :class="item.isManualTree ? 'boq-manual-row__count' : 'bg-blue-darkest'"
                >{{ item.count }}</span>
              </div>
            </td>
          </tr>
          <boq-tree-items
            v-if="hasChildren(item) && ctx.isGroupExpanded(item.id)"
            :key="`certifier-children-${item.id}-${refreshToken}`"
            :project-id="projectId"
            :parent-boq-root-id="item.id"
            :root-context="rootContext"
            :depth="item.depth + 1"
            :refresh-token="ctx.treeRefreshToken(item.id)"
          />
        </template>

        <!-- Recurso (hoja) -->
        <template v-else>
          <tr :key="`resource-${item.id}`" class="hover:bg-grey-lightest">
            <td class="w-8 px-2 py-1 text-center align-middle border border-gray-200">
              <button
                type="button"
                class="inline-flex items-center justify-center w-6 h-6 bg-transparent border-0 cursor-pointer focus:outline-none"
                :aria-expanded="ctx.isItemExpanded(item.id)"
                @click.stop="ctx.toggleItem(item)"
              >
                <b-icon
                  pack="fas"
                  :icon="ctx.isItemExpanded(item.id) ? 'chevron-down' : 'chevron-right'"
                  size="is-small"
                  class="text-grey"
                />
              </button>
            </td>
            <td class="px-2 py-1 text-gray-800 whitespace-nowrap align-middle border border-gray-200">{{ item.code }}</td>
            <td class="max-w-[22rem] px-2 py-1 text-gray-800 truncate align-middle border border-gray-200">{{ item.name }}</td>
            <td class="px-2 py-1 text-gray-800 text-center align-middle border border-gray-200">{{ item.unit }}</td>
            <td class="px-2 py-1 text-gray-800 text-right align-middle border border-gray-200">{{ ctx.formatQuantity(item.total) }}</td>
            <td
              class="px-2 py-1 text-gray-800 text-right align-middle border border-gray-200"
              :class="{ 'font-bold text-red': ctx.isAssignedOver(item) }"
            >
              {{ ctx.formatQuantity(item.assigned) }}
            </td>
            <td
              class="px-2 py-1 text-gray-800 text-right align-middle border border-gray-200"
              :class="{ 'text-red': item.pending < 0 }"
            >
              {{ ctx.formatQuantity(item.pending) }}
            </td>
            <td class="px-2 py-1 text-center align-middle border border-gray-200">
              <button
                type="button"
                class="inline-flex items-center justify-center w-6 h-6 text-white bg-blue border-0 rounded-full cursor-pointer hover:bg-blue-dark focus:outline-none"
                :aria-label="'Subs ' + item.code"
              >
                <b-icon pack="fas" icon="plus" size="is-small" />
              </button>
            </td>
          </tr>
          <tr
            v-if="ctx.isItemExpanded(item.id)"
            :key="`resource-detail-${item.id}`"
          >
            <td colspan="8" class="p-0 bg-grey-lightest border border-gray-300">
              <div v-if="ctx.isResourceLinksLoading(item.id)" class="px-4 py-6 text-center text-gray-500">
                {{ $t('loading') }}...
              </div>

              <!-- Detalle manual: actividades que crearon la medición -->
              <div v-else-if="item.isManualTree" class="px-3 pt-2 pb-3">
                <div class="overflow-hidden bg-white border border-gray-300 rounded">
                  <div class="flex flex-wrap gap-2 justify-between items-center px-2 py-2 boq-manual-detail__header border-b border-gray-300">
                    <div class="flex flex-wrap gap-1.5 items-center min-w-0">
                      <span class="font-bold boq-manual-detail__title uppercase">{{ $t('boq_assignment_activities_title') }}</span>
                      <span class="text-gray-500">
                        · {{ ctx.getResourceAssignmentDetail(item).activities.length }} {{ $t('activities').toLowerCase() }}
                      </span>
                    </div>
                  </div>

                  <div v-if="!ctx.getResourceAssignmentDetail(item).activities.length" class="px-3 py-4 text-center text-xs text-gray-500">
                    {{ $t('no_results') }}
                  </div>

                  <div v-else class="divide-y divide-gray-200">
                    <div
                      v-for="activity in ctx.getResourceAssignmentDetail(item).activities"
                      :key="activity.assignmentId"
                      class="px-3 py-2"
                    >
                      <!-- Subactividad: fila principal + actividad madre anidada -->
                      <template v-if="activity.isSubactivity">
                        <div class="flex flex-wrap gap-2 items-center min-w-0">
                          <span class="boq-tree-items__badge boq-tree-items__badge--subactivity">{{ $t('subactivity').toUpperCase() }}</span>
                          <span class="flex-1 min-w-0 text-xs font-semibold text-gray-800 truncate">{{ activity.name || '—' }}</span>
                          <span v-if="activity.manager" class="flex-shrink-0 text-[0.65rem] text-gray-600 whitespace-nowrap">{{ activity.manager }}</span>
                          <span class="flex-shrink-0 text-[0.65rem] font-semibold text-gray-700 whitespace-nowrap">
                            {{ ctx.formatQuantity(activity.quantity) }}
                            <span v-if="activity.unit">{{ activity.unit }}</span>
                          </span>
                          <span v-if="activity.planningCode" class="flex-shrink-0 text-[0.65rem] font-bold text-blue-dark whitespace-nowrap">{{ activity.planningCode }}</span>
                          <div class="boq-tree-items__progress-wrap">
                            <div class="boq-tree-items__progress-bar">
                              <div
                                class="boq-tree-items__progress-fill"
                                :style="{ width: `${Math.min(100, Math.max(0, activity.progress))}%` }"
                              />
                            </div>
                            <span class="boq-tree-items__progress-label">{{ Math.round(activity.progress) }}%</span>
                          </div>
                        </div>

                        <div
                          v-if="activity.parent"
                          class="mt-2 ml-3 pl-3 border-l-4 boq-manual-detail__parent-border"
                        >
                          <div class="flex flex-wrap gap-2 items-center min-w-0 px-2 py-1.5 boq-manual-detail__parent rounded">
                            <span class="boq-tree-items__badge boq-tree-items__badge--parent">{{ $t('boq_parent_activity_badge') }}</span>
                            <span class="flex-1 min-w-0 text-xs text-gray-800 truncate">
                              <span v-if="activity.parent.planningCode" class="font-semibold">{{ activity.parent.planningCode }}</span>
                              <span v-if="activity.parent.planningCode && activity.parent.name"> </span>
                              <span>{{ activity.parent.name || '—' }}</span>
                            </span>
                            <div class="boq-tree-items__progress-wrap">
                              <div class="boq-tree-items__progress-bar">
                                <div
                                  class="boq-tree-items__progress-fill"
                                  :style="{ width: `${Math.min(100, Math.max(0, activity.parent.progress))}%` }"
                                />
                              </div>
                              <span class="boq-tree-items__progress-label">{{ Math.round(activity.parent.progress) }}%</span>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Actividad padre: solo fila, sin detailed -->
                      <div v-else class="flex flex-wrap gap-2 items-center min-w-0 px-2 py-1.5 boq-manual-detail__parent rounded">
                        <span class="boq-tree-items__badge boq-tree-items__badge--parent">{{ $t('boq_parent_activity_badge') }}</span>
                        <span class="flex-1 min-w-0 text-xs text-gray-800 truncate">
                          <span v-if="activity.planningCode" class="font-semibold">{{ activity.planningCode }}</span>
                          <span v-if="activity.planningCode && activity.name"> </span>
                          <span>{{ activity.name || '—' }}</span>
                        </span>
                        <div class="boq-tree-items__progress-wrap">
                          <div class="boq-tree-items__progress-bar">
                            <div
                              class="boq-tree-items__progress-fill"
                              :style="{ width: `${Math.min(100, Math.max(0, activity.progress))}%` }"
                            />
                          </div>
                          <span class="boq-tree-items__progress-label">{{ Math.round(activity.progress) }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Detalle importado: vinculación de actividades -->
              <div v-else class="px-3 pt-2 pb-3">
                <div class="flex flex-wrap gap-1.5 items-center px-2 py-1 mb-1.5 text-xs bg-blue-lightest border border-blue-lighter rounded">
                  <div class="flex-1 min-w-[10rem] basis-56">
                    <b-autocomplete
                      :key="`activity-ac-${item.id}`"
                      v-model="ctx.getItemLinkForm(item.id).activitySearch"
                      :data="ctx.getActivityAutocompleteOptions(item)"
                      field="label"
                      open-on-focus
                      clearable
                      append-to-body
                      size="is-small"
                      :loading="ctx.isActivitiesLoading || ctx.isActivitySearchLoading(item.id)"
                      :placeholder="$t('select_activity')"
                      class="boq-tree-items__activity-autocomplete"
                      @focus="ctx.onActivityAutocompleteFocus(item)"
                      @typing="ctx.onActivityAutocompleteTyping(item, $event)"
                      @select="ctx.onActivitySelected(item, $event)"
                    >
                      <template v-slot="props">
                        <div class="text-xs leading-tight">
                          <span v-if="props.option.planning_code" class="font-semibold">{{ props.option.planning_code }}</span>
                          <span v-if="props.option.planning_code && props.option.name" class="text-grey-dark"> — </span>
                          <span>{{ props.option.name || `#${props.option.id}` }}</span>
                        </div>
                      </template>
                      <template #empty>{{ $t('no_results') }}</template>
                    </b-autocomplete>
                  </div>
                  <div class="w-20 max-w-[5rem]">
                    <b-input
                      v-model="ctx.getItemLinkForm(item.id).quantity"
                      type="number"
                      step="0.01"
                      min="0"
                      size="is-small"
                      :placeholder="$t('quantity')"
                    />
                  </div>
                  <span class="flex-shrink-0 text-[0.65rem] font-semibold text-blue-dark leading-none">{{ item.unit || '—' }}</span>
                  <span class="flex-1 min-w-[8rem] text-[0.65rem] text-green-darker leading-tight">
                    {{ $t('boq_available_for_new') }}
                    <strong class="font-bold">{{ ctx.formatAvailableQuantity(item) }} {{ item.unit }}</strong>
                  </span>
                  <button
                    type="button"
                    class="inline-flex flex-shrink-0 gap-0.5 items-center px-2 py-0.5 text-[0.6rem] font-bold uppercase leading-none border-0 rounded"
                    :class="ctx.linkButtonClass(item)"
                    :disabled="ctx.isLinkingActivity(item.id) || !ctx.canLinkActivity(item)"
                    @click="ctx.linkActivity(item)"
                  >
                    <b-icon
                      v-if="ctx.isLinkingActivity(item.id)"
                      pack="fas"
                      icon="spinner"
                      custom-class="fa-spin"
                      size="is-small"
                    />
                    <b-icon
                      v-else
                      pack="fas"
                      icon="link"
                      size="is-small"
                    />
                    {{ ctx.isLinkingActivity(item.id) ? $t('boq_linking') : $t('link') }}
                  </button>
                </div>

                <div class="overflow-hidden bg-white border border-gray-300 rounded">
                  <div class="flex flex-wrap gap-2 justify-between items-center px-2 py-2 bg-blue-lightest border-b border-gray-300">
                    <div class="flex flex-wrap gap-1.5 items-center min-w-0">
                      <span class="flex-shrink-0 px-1.5 py-0.5 text-[0.6rem] font-bold text-white uppercase bg-blue-darkest rounded">{{ $t('boq_level_2') }}</span>
                      <span class="font-bold text-blue-dark uppercase">{{ $t('linked_activities') }}</span>
                      <span class="text-gray-500">
                        · {{ ctx.getResourceLinkDetail(item).linkedActivities.length }} {{ $t('activities').toLowerCase() }}
                      </span>
                    </div>
                    <span class="flex-shrink-0 text-[0.7rem] text-blue-dark">
                      {{ $t('boq_reserved_by_activities') }}
                      <strong class="font-bold">{{ ctx.formatQuantity(ctx.getResourceLinkDetail(item).totalReserved) }} {{ item.unit }}</strong>
                    </span>
                  </div>
                  <table class="w-full text-xs border-collapse">
                    <thead>
                      <tr class="bg-blue">
                        <th class="min-w-[14rem] px-2 py-1.5 font-bold text-white text-left uppercase whitespace-nowrap border border-blue-lighter">{{ $t('activity') }}</th>
                        <th class="px-2 py-1.5 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">{{ $t('quantity') }}</th>
                        <th class="px-2 py-1.5 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">{{ $t('assigned') }}</th>
                        <th class="px-2 py-1.5 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">{{ $t('unit') }}</th>
                        <th class="px-2 py-1.5 font-bold text-white text-center uppercase whitespace-nowrap border border-blue-lighter">{{ $t('subactivities') }}</th>
                        <th class="w-10 px-2 py-1.5 border border-blue-lighter" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="!ctx.getResourceLinkDetail(item).linkedActivities.length">
                        <td colspan="6" class="px-2 py-3 text-center text-gray-500 border border-gray-200">
                          {{ $t('no_results') }}
                        </td>
                      </tr>
                      <tr
                        v-for="activity in ctx.getResourceLinkDetail(item).linkedActivities"
                        :key="activity.id"
                        class="hover:bg-grey-lightest"
                      >
                        <td class="max-w-[24rem] px-2 py-1.5 text-gray-800 truncate align-middle border border-gray-200">{{ activity.name }}</td>
                        <td class="px-2 py-1.5 text-right align-middle border border-gray-200">
                          <span class="font-semibold text-blue-dark border-b border-dotted border-blue-dark">{{ ctx.formatQuantity(activity.reserve) }}</span>
                        </td>
                        <td class="px-2 py-1.5 text-gray-800 text-right align-middle border border-gray-200">{{ ctx.formatQuantity(activity.assignedBySubs) }}</td>
                        <td class="px-2 py-1.5 text-gray-800 text-center align-middle border border-gray-200">{{ activity.unit }}</td>
                        <td class="px-2 py-1.5 text-gray-800 text-center align-middle border border-gray-200">{{ activity.subsCount }}</td>
                        <td class="px-2 py-1.5 text-center align-middle border border-gray-200">
                          <button
                            type="button"
                            class="inline-flex items-center justify-center w-6 h-6 bg-transparent border-0 focus:outline-none"
                            :class="ctx.isUnlinkingActivity(item.id, activity.id)
                              ? 'text-gray-400 cursor-wait'
                              : 'text-red cursor-pointer hover:text-red-dark'"
                            :disabled="ctx.isUnlinkingActivity(item.id, activity.id)"
                            :aria-label="$t('unlink')"
                            @click.stop="ctx.unlinkActivity(item, activity)"
                          >
                            <b-icon
                              v-if="ctx.isUnlinkingActivity(item.id, activity.id)"
                              pack="fas"
                              icon="spinner"
                              custom-class="fa-spin"
                              size="is-small"
                            />
                            <b-icon
                              v-else
                              pack="fas"
                              icon="trash-alt"
                              size="is-small"
                            />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BoqTreeItems',

  components: {
    BoqTreeItems: null,
  },

  beforeCreate() {
    this.$options.components.BoqTreeItems = this.$options
  },

  inject: {
    boqTreeContext: {
      default: null,
    },
  },

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    parentBoqRootId: {
      type: [Number, String],
      required: true,
    },
    rootContext: {
      type: Object,
      required: true,
    },
    depth: {
      type: Number,
      default: 1,
    },
    refreshToken: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      items: [],
      loading: false,
    }
  },

  computed: {
    ctx() {
      return this.boqTreeContext
    },
  },

  watch: {
    parentBoqRootId() {
      this.loadItems()
    },
    refreshToken() {
      this.loadItems()
    },
  },

  mounted() {
    this.loadItems()
  },

  methods: {
    async loadItems() {
      if (!this.projectId || this.parentBoqRootId == null) {
        this.items = []
        return
      }

      this.loading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.projectId}/boq-roots`,
          { params: { parent_id: this.parentBoqRootId } }
        )

        if (data && data.success) {
          this.items = (data.boq_roots || []).map((node) => (
            this.ctx.mapGroupItem(node, this.rootContext, this.depth)
          ))
          this.ctx.reloadExpandedResourceLinks(this.items)
        } else {
          this.items = []
        }
      } catch (error) {
        this.items = []
        this.$notify.error('error_loading_data')
      } finally {
        this.loading = false
      }
    },

    hasChildren(item) {
      // Stage takeoff: hoja; no hay hijos BOQ debajo (las mediciones no son nodos del árbol).
      if (item && item.nodeType === 'takeoff_node' && Number(item.rowKind) === 2) {
        return false
      }
      if (item && item.nodeType === 'takeoff_node') {
        return Number(item.childrenCount || 0) > 0
      }
      return Number(item.count || 0) > 0
    },

    indentStyle(depth) {
      const level = depth != null ? depth : 0
      if (level <= 0) {
        return null
      }
      return { paddingLeft: `${level * 1.25}rem` }
    },
  },
}
</script>

<style scoped>
.boq-tree-items__activity-autocomplete :deep(.control) {
  width: 100%;
}

.boq-tree-items__badge {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.6rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.02em;
}

.boq-tree-items__badge--subactivity {
  background-color: #7c3aed;
}

.boq-tree-items__badge--parent {
  background-color: #3b66bc;
}

.boq-tree-items__progress-wrap {
  display: inline-flex;
  flex-shrink: 0;
  gap: 0.375rem;
  align-items: center;
  min-width: 5.5rem;
}

.boq-tree-items__progress-bar {
  width: 3.5rem;
  height: 0.375rem;
  overflow: hidden;
  background-color: #e5e7eb;
  border-radius: 9999px;
}

.boq-tree-items__progress-fill {
  height: 100%;
  background-color: #3b66bc;
  border-radius: 9999px;
}

.boq-tree-items__progress-label {
  min-width: 2rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: #374151;
  text-align: right;
}

.boq-manual-row {
  background-color: #ede9fe;
}

.boq-manual-row:hover {
  background-color: #e4d9ff;
}

.boq-manual-row--nested {
  background-color: #f5f3ff;
}

.boq-manual-row--nested:hover {
  background-color: #ede9fe;
}

.boq-manual-row__chevron {
  color: #6d28d9;
}

.boq-manual-row__code-badge {
  background-color: #7c3aed;
}

.boq-manual-row__title {
  color: #5b21b6;
}

.boq-manual-row__meta {
  color: #7c3aed;
}

.boq-manual-row__action-btn {
  background-color: #7c3aed;
}

.boq-manual-row__action-btn:hover {
  background-color: #6d28d9;
}

.boq-manual-row__count {
  background-color: #5b21b6;
}

.boq-takeoff-row {
  background-color: #ecfdf5;
}

.boq-takeoff-row:hover {
  background-color: #d1fae5;
}

.boq-takeoff-row--nested {
  background-color: #f0fdfa;
}

.boq-takeoff-row--nested:hover {
  background-color: #ecfdf5;
}

.boq-takeoff-row__chevron {
  color: #0f766e;
}

.boq-takeoff-row__code-badge {
  background-color: #0f766e;
}

.boq-takeoff-row__title {
  color: #115e59;
}

.boq-takeoff-row__meta {
  color: #0f766e;
}

.boq-takeoff-row__action-btn {
  background-color: #0f766e;
}

.boq-takeoff-row__action-btn:hover {
  background-color: #0d9488;
}

.boq-takeoff-row__action-btn--danger {
  background-color: #b91c1c;
}

.boq-takeoff-row__action-btn--danger:hover {
  background-color: #dc2626;
}

.boq-takeoff-row__count {
  background-color: #115e59;
}

.boq-manual-detail__header {
  background-color: #f5f3ff;
}

.boq-manual-detail__title {
  color: #5b21b6;
}

.boq-manual-detail__parent {
  background-color: #f5f3ff;
}

.boq-manual-detail__parent-border {
  border-color: #7c3aed;
}
</style>

<style>
/* append-to-body: menú fuera del panel con overflow del árbol BOQ */
body > .autocomplete.control .dropdown-menu {
  z-index: 120 !important;
}
</style>
